import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LockSimple, BookOpen, Warning, CalendarBlank,
  CheckCircle, Circle, CaretDown, CaretRight,
  Info, Sparkle, Plus, CreditCard, ArrowRight, CircleNotch, X,
} from "@phosphor-icons/react";
import { Mascot }         from "@/components/styleguide/Mascot";
import { WordInsightsModal } from "./WordInsightsModal";
import { BloomButton }    from "@/components/styleguide/ui/BloomButton";
import { BloomInput }     from "@/components/styleguide/ui/BloomInput";
import { BloomBadge }     from "@/components/styleguide/ui/BloomBadge";
import { ChildAvatar }    from "@/experiences/select-child/ChildAvatar";
import { cn }             from "@/lib/utils";
import { api }            from "./api";
import { useAuth }        from "./AuthContext";
import { VocabHeader }    from "./VocabHeader";
import { NotificationFeed } from "./NotificationFeed";
import TrialEndedModal    from "./TrialEndedModal";

/* ── Small fetch hook ─────────────────────────────────────────────────── */
function useApi(path, refreshKey = 0, skip = false) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (skip) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    api(path)
      .then(d  => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [path, refreshKey, skip]);

  return { data, loading };
}

/* ── PIN card ─────────────────────────────────────────────────────────── */
function PinCard() {
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [pin,     setPin]     = useState("");
  const [msg,     setMsg]     = useState(null);
  const [saving,  setSaving]  = useState(false);

  const save = async () => {
    setMsg(null);
    if (!/^\d{4}$/.test(pin)) { setMsg({ err: true, text: "PIN must be exactly 4 digits" }); return; }
    setSaving(true);
    try {
      await api("/auth/pin", { method: "POST", body: { pin, current_pin: current || null } });
      await refresh();
      setPin(""); setCurrent("");
      setMsg({ err: false, text: "PIN saved!" });
    } catch (e) {
      setMsg({ err: true, text: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-3xl border border-border bg-card p-5 shadow-soft" data-testid="pin-card">
      <div className="flex items-center gap-2">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent">
          <LockSimple size={18} weight="bold" className="text-primary-strong" />
        </span>
        <div>
          <p className="font-display text-base font-black text-foreground">Parent PIN</p>
          <p className="text-xs font-semibold text-muted-foreground">
            {user?.has_pin ? "Protects Parent Mode from little fingers." : "Set a 4-digit PIN to protect Parent Mode."}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        {user?.has_pin && (
          <BloomInput label="Current PIN" type="password" inputMode="numeric" maxLength={4}
            value={current} data-testid="pin-current-input"
            onChange={e => setCurrent(e.target.value.replace(/\D/g, ""))} placeholder="••••" />
        )}
        <BloomInput
          label={user?.has_pin ? "New PIN" : "Choose a PIN"}
          type="password" inputMode="numeric" maxLength={4}
          value={pin} data-testid="pin-new-input"
          onChange={e => setPin(e.target.value.replace(/\D/g, ""))} placeholder="••••"
        />
        <BloomButton size="sm" onClick={save} disabled={saving} data-testid="pin-save-btn" className="shrink-0">
          {saving ? "Saving…" : user?.has_pin ? "Update PIN" : "Set PIN"}
        </BloomButton>
      </div>
      {msg && (
        <p className={`mt-2 text-sm font-bold ${msg.err ? "text-destructive" : "text-success-ink"}`}
           data-testid="pin-msg">
          {msg.text}
        </p>
      )}
    </div>
  );
}

/* ── Quiz Help toggle ─────────────────────────────────────────────────── */
function QuizHelpToggle({ child }) {
  const [enabled, setEnabled] = useState(child.quiz_help_enabled || false);
  const [saving,  setSaving]  = useState(false);

  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    setSaving(true);
    try {
      await api(`/children/${child.child_id}`, { method: "PATCH", body: { quiz_help_enabled: next } });
    } catch {
      setEnabled(!next);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex shrink-0 flex-col items-end gap-0.5">
      <button
        onClick={toggle}
        disabled={saving}
        aria-label={`Quiz help ${enabled ? "on" : "off"} for ${child.first_name}`}
        className="flex items-center gap-1.5 rounded-full transition hover:opacity-80"
      >
        <span className="hidden text-[10px] font-extrabold text-muted-foreground sm:inline">Quiz help</span>
        <span className={cn(
          "relative inline-flex h-5 w-9 rounded-full transition-colors duration-200",
          enabled ? "bg-teal" : "bg-border",
          saving && "opacity-60",
        )}>
          <span className={cn(
            "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all duration-200",
            enabled ? "left-[18px]" : "left-0.5",
          )} />
        </span>
      </button>
      <span className="max-w-[88px] text-right text-[9px] font-semibold leading-tight text-muted-foreground">
        {enabled ? "Word cards on wrong answers" : "Help cards off"}
      </span>
    </div>
  );
}

/* ── Word Insight tiles ───────────────────────────────────────────────── */
const INSIGHT_TILES = [
  { key: "confident",  label: "Knows well",     icon: CheckCircle, tint: "text-teal-ink",       bg: "bg-teal/10",        rule: "Quizzed 3+ times, 70%+ correct" },
  { key: "struggling", label: "Struggling",     icon: Warning,     tint: "text-destructive",    bg: "bg-destructive/10", rule: "Quizzed 2+ times, under 50% correct" },
  { key: "learning",   label: "Still learning", icon: BookOpen,    tint: "text-primary-strong", bg: "bg-primary/10",     rule: "Seen, but too few quizzes to judge, or 50–69% correct" },
  { key: "never_seen", label: "Never seen",     icon: Circle,      tint: "text-muted-foreground", bg: "bg-muted/60",     rule: "Not opened in Learn yet" },
];

function InsightTiles({ child, insights, loading, onTargetCreated }) {
  const [openCategory, setOpenCategory] = useState(null);
  const [showHow,      setShowHow]      = useState(false);

  if (loading) return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {[0, 1, 2, 3].map(i => <div key={i} className="h-24 animate-pulse rounded-2xl bg-muted" />)}
    </div>
  );

  if (!insights) return (
    <p className="text-xs font-semibold text-muted-foreground">
      Word insights couldn't load. Refresh to try again.
    </p>
  );

  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {INSIGHT_TILES.map(({ key, label, icon: Icon, tint, bg }) => (
          <button
            key={key}
            onClick={() => setOpenCategory(key)}
            className={cn("rounded-2xl p-3 text-center transition active:scale-[0.98] hover:brightness-95", bg)}
            data-testid={`insight-tile-${key}`}
          >
            <Icon size={18} weight="fill" className={cn("mx-auto", tint)} />
            <p className="mt-1 font-display text-2xl font-black text-foreground tabular-nums">
              {insights.counts[key]}
            </p>
            <p className={cn("text-[10px] font-bold uppercase tracking-wide", tint)}>{label}</p>
          </button>
        ))}
      </div>

      <button
        onClick={() => setShowHow(s => !s)}
        aria-expanded={showHow}
        className="mt-2 flex items-center gap-1 text-[11px] font-extrabold text-muted-foreground hover:text-foreground"
      >
        <Info size={13} weight="bold" />
        How are these worked out?
      </button>

      <AnimatePresence initial={false}>
        {showHow && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <div className="mt-2 space-y-1 rounded-xl bg-muted/50 px-3 py-2.5 text-[11px] font-semibold text-muted-foreground">
              {INSIGHT_TILES.map(({ key, label, tint, rule }) => (
                <p key={key}>
                  <span className={cn("font-extrabold", tint)}>{label}:</span> {rule}
                </p>
              ))}
              <p className="pt-1">Every quiz answer counts, right or wrong.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {openCategory && (
          <WordInsightsModal
            child={child}
            category={openCategory}
            words={insights[openCategory]}
            onClose={() => setOpenCategory(null)}
            onTargetCreated={() => {
              setOpenCategory(null);
              onTargetCreated?.();  // navigates to create plan page
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

/* ── Plans section ────────────────────────────────────────────────────── */
function PlansSection({ child, plans, navigate }) {
  const hasPlans  = !plans.loading && plans.data?.length > 0;
  const isLoading = plans.loading;

  /* Loading skeleton */
  if (isLoading) {
    return (
      <div className="mt-4 border-t border-border pt-4">
        <div className="h-16 animate-pulse rounded-2xl bg-muted" />
      </div>
    );
  }

  /* ── No plans yet: eye-catching animated CTA ── */
  if (!hasPlans) {
    return (
      <div className="mt-4 border-t border-border pt-4">
        <motion.button
          onClick={() => navigate(`/vocab/parent/child/${child.child_id}/plans/create`)}
          animate={{
            boxShadow: [
              "0 0 0 0px rgba(255, 122, 69, 0.0)",
              "0 0 0 6px rgba(255, 122, 69, 0.22)",
              "0 0 0 0px rgba(255, 122, 69, 0.0)",
            ],
          }}
          transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
          className="w-full rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/8 via-accent/60 to-secondary/10 p-4 text-left transition hover:brightness-[1.03] active:scale-[0.98]"
        >
          <div className="flex items-center gap-3">
            {/* Pulsing icon */}
            <motion.span
              animate={{ scale: [1, 1.12, 1] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-md"
            >
              <CalendarBlank size={20} weight="fill" />
            </motion.span>

            {/* Copy */}
            <div className="min-w-0 flex-1">
              <p className="font-display text-sm font-black text-foreground">
                Create a plan for {child.first_name}
              </p>
              <p className="mt-0.5 text-xs font-semibold text-muted-foreground">
                Give {child.first_name} a focused set of words to practise
              </p>
            </div>

            {/* Plus chip */}
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow">
              <Plus size={15} weight="bold" />
            </span>
          </div>
        </motion.button>
      </div>
    );
  }

  /* ── Has plans: prominent navigation row ── */
  const totalWords = plans.data.reduce((s, p) => s + (p.word_ids?.length ?? 0), 0);
  const planCount  = plans.data.length;

  return (
    <div className="mt-4 border-t border-border pt-4">
      <button
        onClick={() => navigate(`/vocab/parent/child/${child.child_id}/plans`)}
        className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3.5 text-left shadow-sm transition hover:border-primary/30 hover:bg-primary/5 hover:shadow-md active:scale-[0.99]"
      >
        {/* Icon */}
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/12">
          <CalendarBlank size={18} weight="fill" className="text-primary" />
        </span>

        {/* Info */}
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-black text-foreground">Plans</p>
          <p className="truncate text-xs font-semibold text-muted-foreground">
            {planCount} plan{planCount !== 1 ? "s" : ""} · {totalWords} word{totalWords !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Arrow */}
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <CaretRight size={14} weight="bold" />
        </span>
      </button>
    </div>
  );
}

/* ── Child card ───────────────────────────────────────────────────────── */
function ChildCard({ child }) {
  const navigate   = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const insights = useApi(`/children/${child.child_id}/words/insights`, 0, !expanded);
  const plans    = useApi(`/children/${child.child_id}/plans`,           0, !expanded);

  return (
    <div
      className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft"
      data-testid={`child-stats-${child.child_id}`}
    >
      {/* Always-visible header */}
      <button
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-3 px-5 py-4 text-left transition hover:bg-muted/30"
      >
        <ChildAvatar variant={child.avatar} className="h-12 w-12 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-display text-lg font-black text-foreground">{child.first_name}</p>
          <BloomBadge variant="neutral" className="mt-0.5 px-2.5 py-0.5 text-xs">
            Year {child.school_year}
          </BloomBadge>
        </div>
        {/* Quiz help toggle — stop propagation so it doesn't toggle the card */}
        <div onClick={e => e.stopPropagation()}>
          <QuizHelpToggle child={child} />
        </div>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground transition hover:bg-border">
          <CaretDown
            size={14} weight="bold"
            className={cn("transition-transform duration-200", expanded && "rotate-180")}
          />
        </span>
      </button>

      {/* Collapsible body */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={  { height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <div className="px-5 pb-5">
              {/* Word insight tiles */}
              <InsightTiles
                child={child}
                insights={insights.data}
                loading={insights.loading}
                onTargetCreated={() =>
                  navigate(`/vocab/parent/child/${child.child_id}/plans/create`)
                }
              />

              {/* Plans section — animated CTA or navigation row */}
              <PlansSection child={child} plans={plans} navigate={navigate} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Trial urgency banner (top strip, ≤3 days left) ──────────────────── */
function TrialBanner({ user, onSubscribe }) {
  if (!user) return null;
  if (user.subscription_status !== "trialing") return null;

  const days = user.days_left;
  const urgent = days <= 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
      className={cn(
        "mt-5 flex items-center justify-between gap-3 rounded-2xl px-4 py-3",
        urgent
          ? "border border-destructive/20 bg-destructive/8"
          : "border border-warning/20 bg-warning/8",
      )}
    >
      <p className={cn("text-sm font-semibold", urgent ? "text-destructive" : "text-warning-ink")}>
        {days === 0
          ? "⏰ Your free trial ends today — subscribe to keep access."
          : days === 1
          ? "⏰ 1 day left in your free trial."
          : `${days} days left in your free trial.`}
      </p>
      <button
        onClick={onSubscribe}
        className={cn(
          "flex shrink-0 items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-extrabold transition",
          urgent
            ? "bg-destructive text-white hover:bg-destructive/90"
            : "bg-warning-ink text-white hover:opacity-90",
        )}
      >
        Subscribe <ArrowRight size={12} weight="bold" />
      </button>
    </motion.div>
  );
}

/* ── Billing card (bottom, full plan management) ──────────────────────── */
function BillingCard({ forceExpired = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cancelling,    setCancelling]    = useState(false);
  const [upgrading,     setUpgrading]     = useState(false);
  const [done,          setDone]          = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  if (!user) return null;

  const status    = user.subscription_status;
  const plan      = user.plan;           // "monthly" | "annual" | null
  const periodEnd = user.stripe_subscription_period_end;
  const trialEnd  = user.trial_end;
  const daysLeft  = user.days_left;

  function formatDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  }

  async function handleCancel() {
    if (!window.confirm("Cancel subscription? You'll keep access until your current period ends.")) return;
    setCancelling(true);
    try {
      await api("/billing/cancel", { method: "POST" });
      setDone(true);
    } catch (err) {
      alert(err?.message ?? "Couldn't cancel. Please try again.");
    } finally {
      setCancelling(false);
    }
  }

  async function handleChoosePlan(planId = "annual") {
    setUpgrading(true);
    setCheckoutError(null);
    try {
      const res = await api("/billing/checkout", { method: "POST", body: { plan: planId } });
      if (!res?.url) throw new Error("No checkout URL returned");
      window.location.href = res.url;
    } catch (err) {
      console.error("Checkout error:", err);
      setUpgrading(false);
      setCheckoutError(err?.message ?? "Something went wrong. Please try again.");
    }
  }

  // ── No active subscription: trialing, expired, or payment failed ───────
  // forceExpired=true means we always show the "Trial ended" plan-picker UI
  // (used in the billing sheet so clicking Subscribe shows the same UI as expired)
  if (!plan || status === "trialing" || status === "expired" || status === "payment_failed" || forceExpired) {
    const expired = forceExpired || status === "expired" || status === "payment_failed";
    return (
      <div className="rounded-3xl border border-border bg-card p-5 shadow-soft" data-testid="billing-card">
        <div className="flex items-center gap-2">
          <span className={`flex h-10 w-10 items-center justify-center rounded-2xl ${expired ? "bg-destructive/10" : "bg-warning/12"}`}>
            <CreditCard size={18} weight="bold" className={expired ? "text-destructive" : "text-warning-ink"} />
          </span>
          <div>
            <p className="font-display text-base font-black text-foreground">
              {expired ? (status === "payment_failed" ? "Payment failed" : "Trial ended") : "Free trial"}
            </p>
            <p className="text-xs font-semibold text-muted-foreground">
              {expired
                ? "Choose a plan to keep access"
                : daysLeft === 0
                  ? "Today is your last day"
                  : `${daysLeft} day${daysLeft !== 1 ? "s" : ""} remaining · ends ${formatDate(trialEnd)}`}
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <button
            onClick={() => handleChoosePlan("annual")}
            disabled={upgrading}
            className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-3 font-display text-sm font-extrabold text-white shadow-sm transition hover:bg-primary/90 disabled:opacity-60"
          >
            {upgrading
              ? <><CircleNotch size={15} className="animate-spin" /> Redirecting…</>
              : <>Annual — £60/yr · best value <ArrowRight size={15} weight="bold" /></>}
          </button>
          <button
            onClick={() => handleChoosePlan("monthly")}
            disabled={upgrading}
            className="flex flex-1 items-center justify-center gap-2 rounded-2xl border border-border bg-muted/40 px-4 py-3 font-display text-sm font-extrabold text-foreground transition hover:bg-muted disabled:opacity-60"
          >
            Monthly — £6.49/mo
          </button>
        </div>
        {checkoutError && (
          <p className="mt-3 text-center text-xs font-bold text-destructive">
            {checkoutError}
          </p>
        )}
      </div>
    );
  }

  // ── Active or cancelled subscription ──────────────────────────────────
  const isCancelled = status === "cancelled";
  const planLabel   = plan === "annual" ? "Annual · £60/yr" : "Monthly · £6.49/mo";

  return (
    <div className="rounded-3xl border border-border bg-card p-5 shadow-soft" data-testid="billing-card">
      <div className="flex items-center gap-2">
        <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-success/12">
          <CreditCard size={18} weight="bold" className="text-success-ink" />
        </span>
        <div>
          <p className="font-display text-base font-black text-foreground">Subscription</p>
          <p className="text-xs font-semibold text-muted-foreground">{planLabel}</p>
        </div>
        <span className={`ml-auto rounded-full px-3 py-1 text-xs font-extrabold ${isCancelled ? "bg-warning/12 text-warning-ink" : "bg-success/12 text-success-ink"}`}>
          {isCancelled ? "Cancels at period end" : "Active"}
        </span>
      </div>

      {periodEnd && (
        <div className="mt-4 flex items-center gap-2 rounded-2xl bg-muted/50 px-4 py-3">
          <CalendarBlank size={15} weight="bold" className="shrink-0 text-muted-foreground" />
          <p className="text-sm font-semibold text-foreground">
            {isCancelled
              ? <>Access until <span className="font-extrabold">{formatDate(periodEnd)}</span></>
              : <>Renews <span className="font-extrabold">{formatDate(periodEnd)}</span></>}
          </p>
        </div>
      )}

      {done && (
        <p className="mt-3 text-sm font-bold text-success-ink">
          Cancellation confirmed — you'll keep access until {formatDate(periodEnd)}.
        </p>
      )}

      {!isCancelled && !done && (
        <button
          onClick={handleCancel}
          disabled={cancelling}
          className="mt-4 w-full rounded-2xl border border-border py-2.5 text-sm font-extrabold text-muted-foreground transition hover:border-destructive/40 hover:text-destructive disabled:opacity-50"
        >
          {cancelling ? "Cancelling…" : "Cancel subscription"}
        </button>
      )}
    </div>
  );
}

/* ── Main page ────────────────────────────────────────────────────────── */
export default function ParentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stats,               setStats]               = useState(null);
  const [showPinHint,         setShowPinHint]         = useState(false);
  const [showNoChildrenHint,  setShowNoChildrenHint]  = useState(false);
  const [showWordLimitHint,   setShowWordLimitHint]   = useState(false);
  const [showBillingSheet,    setShowBillingSheet]    = useState(false);

  // True only once stats have loaded and the array is confirmed empty
  const hasNoChildren = stats !== null && stats.length === 0;

  const isTrialing  = user?.subscription_status === "trialing";
  const wordUsage   = useApi("/parent/words/ai/usage", 0, !isTrialing);

  useEffect(() => {
    api("/parent/stats").then(setStats).catch(() => setStats([]));
  }, []);

  // Auto-show billing sheet when trial has expired — cannot be dismissed
  useEffect(() => {
    if (user?.subscription_status === "expired") {
      setShowBillingSheet(true);
    }
  }, [user?.subscription_status]);

  const handleRequestWord = () => {
    if (!user?.has_pin) {
      setShowPinHint(true);
      setTimeout(() => setShowPinHint(false), 5000);
      return;
    }
    // Guard: no children yet — word requests must be linked to at least one child
    if (hasNoChildren) {
      setShowNoChildrenHint(true);
      setTimeout(() => setShowNoChildrenHint(false), 6000);
      return;
    }
    if (isTrialing && wordUsage.data?.at_limit) {
      setShowWordLimitHint(true);
      setTimeout(() => setShowWordLimitHint(false), 6000);
      return;
    }
    navigate("/vocab/parent/word-requests");
  };

  return (
    <div className="min-h-[100svh] bg-background">
      <VocabHeader onSubscribe={() => setShowBillingSheet(true)} />
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">

        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-display text-3xl font-black tracking-tight text-foreground sm:text-4xl">
              Parent Dashboard
            </h1>
            <p className="mt-1 text-sm font-semibold text-muted-foreground">
              Watch every child's vocabulary bloom.
            </p>
          </div>

          <div className="flex w-full items-center gap-2 sm:w-auto sm:shrink-0 sm:pt-1">
            <div className="relative">
              <BloomButton
                size="sm"
                leftIcon={Sparkle}
                onClick={handleRequestWord}
                className={cn((!user?.has_pin || hasNoChildren) && "opacity-60")}
                aria-disabled={!user?.has_pin || hasNoChildren}
                data-testid="request-word-btn"
              >
                <span className="hidden sm:inline">Request a word</span>
                <span className="sm:hidden">Request</span>
              </BloomButton>

              {/* Trial usage counter */}
              {isTrialing && !wordUsage.loading && wordUsage.data && (
                <p className={`mt-1 text-center text-[10px] font-bold ${
                  wordUsage.data.at_limit ? "text-destructive" : "text-muted-foreground"
                }`}>
                  {wordUsage.data.remaining}/{wordUsage.data.limit} requests today
                </p>
              )}

              <AnimatePresence>
                {showPinHint && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    exit={{   opacity: 0, y: 6 }} transition={{ duration: 0.18 }}
                    className="absolute left-0 top-full z-50 mt-2 w-64 rounded-2xl border border-warning/40 bg-card px-4 py-3 shadow-lg sm:left-auto sm:right-0"
                  >
                    <p className="text-xs font-semibold leading-relaxed text-warning-ink">
                      Set a parent PIN first — this keeps word requests out of little hands.
                    </p>
                    <p className="mt-1 text-xs font-bold text-warning-ink">See the PIN section below ↓</p>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {showNoChildrenHint && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    exit={{   opacity: 0, y: 6 }} transition={{ duration: 0.18 }}
                    className="absolute left-0 top-full z-50 mt-2 w-64 rounded-2xl border border-primary/30 bg-card px-4 py-3 shadow-lg sm:left-auto sm:right-0"
                  >
                    <p className="text-xs font-semibold leading-relaxed text-foreground">
                      Add a child first — word requests are linked to a child's learning list.
                    </p>
                    <p className="mt-1 text-xs font-bold text-primary-strong">Go to the Children tab to add a child →</p>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {showWordLimitHint && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    exit={{   opacity: 0, y: 6 }} transition={{ duration: 0.18 }}
                    className="absolute left-0 top-full z-50 mt-2 w-[min(288px,calc(100vw-2rem))] rounded-2xl border border-destructive/30 bg-card px-4 py-3 shadow-lg sm:left-auto sm:right-0"
                  >
                    <p className="text-xs font-semibold leading-relaxed text-destructive-ink">
                      Trial accounts can request up to {wordUsage.data?.limit ?? 2} words per day. Subscribe for a higher daily limit. Resets tomorrow.
                    </p>
                    <button
                      onClick={() => { setShowWordLimitHint(false); setShowBillingSheet(true); }}
                      className="mt-2 text-xs font-extrabold text-destructive-ink underline"
                    >
                      Subscribe for more requests →
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="ml-auto sm:ml-0">
              <NotificationFeed />
            </div>
          </div>
        </div>

        {/* Trial urgency banner */}
        <TrialBanner user={user} onSubscribe={() => setShowBillingSheet(true)} />

        {/* Per-child cards */}
        <div className="mt-7 space-y-5">
          {stats === null && <div className="h-40 animate-pulse rounded-3xl bg-muted" />}

          {stats?.map(({ child }) => (
            <ChildCard key={child.child_id} child={child} />
          ))}

          {stats?.length === 0 && (
            <div className="flex flex-col items-center gap-2 rounded-3xl border border-dashed border-border p-8 text-center">
              <Mascot size={56} mood="happy" animated={false} />
              <p className="text-sm font-semibold text-muted-foreground">
                Add a child to start tracking their vocabulary journey.
              </p>
            </div>
          )}
        </div>

        <div className="mt-6"><PinCard /></div>

      </main>

      {/* Trial ended modal — shown when Subscribe is clicked or trial expires */}
      <AnimatePresence>
        {showBillingSheet && (
          <TrialEndedModal
            canDismiss={user?.subscription_status !== "expired"}
            onDismiss={() => setShowBillingSheet(false)}
          />
        )}
      </AnimatePresence>

    </div>
  );
}